import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { IBellArrival, registerBellTarget } from '@services/backgroundJobs/flyToBell';
import { BELL_HINT_MS, bumpBellHintCount, readBellHintCount, shouldShowBellHint } from './bellHint';
import { BellJobFx } from './bellJobFx';

export interface IUseBellLandingOptions {
    userId?: number | string;
    /** Кнопка колокольчика — цель полёта. */
    targetRef: RefObject<HTMLElement | null>;
    dropdownOpen: boolean;
    /** У колокольчика стопка превью уведомлений. */
    toastsShown: boolean;
    play: (fx: BellJobFx, jobId: string) => void;
}

/**
 * Колокольчик — цель `flyToBell`: на прилёт задачи отвечает сценой `arrive` и первые разы подсказкой, где следить за
 * её ходом. Подсказка уходит сама, по открытию колокольчика и когда у него появляются превью уведомлений.
 */
export const useBellLanding = ({ userId, targetRef, dropdownOpen, toastsShown, play }: IUseBellLandingOptions) => {
    const [hint, setHint] = useState<IBellArrival | null>(null);
    const timerRef = useRef<number | null>(null);
    const latest = useRef({ userId, dropdownOpen, toastsShown, play });
    latest.current = { userId, dropdownOpen, toastsShown, play };

    const closeHint = useCallback(() => {
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        timerRef.current = null;
        setHint(null);
    }, []);

    useEffect(() => {
        const element = targetRef.current;
        if (!element || !userId) return undefined;
        const unregister = registerBellTarget({
            element,
            onArrive: (arrival) => {
                const current = latest.current;
                current.play('arrive', '');
                const id = current.userId;
                if (
                    !id ||
                    !shouldShowBellHint({
                        shownCount: readBellHintCount(id),
                        dropdownOpen: current.dropdownOpen,
                        toastsShown: current.toastsShown,
                    })
                ) {
                    return;
                }
                bumpBellHintCount(id);
                closeHint();
                setHint(arrival);
                timerRef.current = window.setTimeout(closeHint, BELL_HINT_MS);
            },
        });
        return () => {
            unregister();
            closeHint();
        };
    }, [targetRef, userId, closeHint]);

    useEffect(() => {
        if (dropdownOpen || toastsShown) closeHint();
    }, [dropdownOpen, toastsShown, closeHint]);

    return { hint, closeHint };
};
